import React, { useMemo, useState } from "react";
import { PageHeader, Card } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable, updateAirtable } from "../hooks/useAirtable";
import { money, num, str, fmtDate, arr, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";

/**
 * Account History Positioning (formerly "Authorized User Credit Positioning" /
 * "Tradelines"). JC3 sells education and strategy guidance about profile
 * positioning — no account access, no authorized-user placement, no bureau
 * disputes. Airtable field IDs remain unchanged (per user decision 1a); only
 * the customer-facing display labels are updated.
 */
const ProfilePositioningPage = () => {
  const positioning = useAirtable("tradelines"); // Airtable slug unchanged
  const clients = useAirtable("clients");
  const [filter, setFilter] = useState("All");
  const [busyId, setBusyId] = useState(null);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);

  if (positioning.loading || clients.loading) return <Loading label="Loading positioning records" />;
  if (positioning.error) return <ErrorState message={positioning.error} onRetry={positioning.refetch} />;

  const rows = positioning.records
    .map((r) => {
      const f = r.fields || {};
      return {
        id: r.id,
        client: linkedName(f["Client Link"], clientMap),
        name: str(f["Tradeline Name"]),
        limit: money(f["Credit Limit"]),
        dateAdded: f["Date Added"],
        reportingDate: f["Reporting Date"],
        daysUntil: num(f["Days Until Reporting"]),
        agencies: arr(f["Bureaus Reporting"]).join(" · "),
        reported: !!f["Reported"],
      };
    })
    .sort((a, b) => (a.daysUntil ?? 99999) - (b.daysUntil ?? 99999));

  const visible = rows.filter((r) => {
    if (!filterByDate(r.reportingDate)) return false;
    if (filter === "Reported") return r.reported;
    if (filter === "Pending") return !r.reported;
    return true;
  });

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      await updateAirtable("tradelines", row.id, { "Reported": !row.reported });
      await positioning.refetch();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="tradeline-tracker-page">
      <PageHeader
        eyebrow="DASHBOARD / ACCOUNT HISTORY POSITIONING"
        title="Account History Positioning"
        subtitle={`${visible.length} of ${rows.length} account records • ${scope === "period" ? "filtered by Reporting Date" : "all-time view"} • Sorted by reporting date ascending.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="tradeline-scope" />
            <ExportButtons
              title="Account History Positioning"
              filename="profile-positioning-services"
              testidPrefix="export-tradelines"
              columns={[
                { key: "client", label: "Client" },
                { key: "name", label: "Account name" },
                { key: "limit", label: "Account limit" },
                { key: "reportingDate", label: "Reporting Date" },
                { key: "daysUntil", label: "Days Until" },
                { key: "agencies", label: "Reporting Agencies" },
                { key: "reported", label: "Reported", get: (r) => (r.reported ? "Yes" : "No") },
              ]}
              rows={visible}
            />
            {["All","Pending","Reported"].map((f) => (
              <button
                key={f}
                data-testid={`tradeline-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 border font-heading tracking-widest text-[0.75rem] uppercase ${filter === f ? "border-[#2A1C15] bg-[#2A1C15] text-[#DCCFB9]" : "border-[rgba(42,28,21,0.18)] text-[#2A1C15] hover:border-[#2A1C15]"}`}
              >{f}</button>
            ))}
          </div>
        }
      />

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left">
              {["Client","Account name","Account limit","Reporting Date","Days Until","Reporting Agencies","Reported"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#5E4D40] text-sm">No account records match this filter.</td></tr>}
            {visible.map((r, i) => {
              const days = r.daysUntil;
              const isUrgent = !r.reported && days !== null && days >= 0 && days < 7;
              const isPast = days !== null && days < 0;
              return (
                <tr key={r.id} data-testid={`tradeline-row-${i}`} className="bj-table-row">
                  <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.client}</td>
                  <td className="py-3 pr-3 text-[#6E5022] text-sm">{r.name}</td>
                  <td className="py-3 pr-3 text-[#2A1C15] text-sm bj-tnum">${r.limit.toLocaleString()}</td>
                  <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(r.reportingDate)}</td>
                  <td className={`py-3 pr-3 text-sm bj-tnum font-heading text-lg ${isUrgent ? "text-[#6E5022]" : isPast ? "text-[#5E4D40]" : "text-[#2A1C15]"}`}>
                    {days === null ? "—" : isPast ? `${Math.abs(days)}d ago` : `${days}d`}
                  </td>
                  <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.agencies || "—"}</td>
                  <td className="py-3 pr-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`tradeline-reported-${i}`}
                        checked={r.reported}
                        disabled={busyId === r.id}
                        onChange={() => toggle(r)}
                        className="w-4 h-4 accent-[#6E5022]"
                      />
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default ProfilePositioningPage;
